import Paper from "@mui/material/Paper";
import axios from "axios";
import moment from "moment-timezone";

import { Chip } from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import { useState } from "react";
import { app } from "../signals/AppSignal";
import { parsePlan } from "../utils/StringUtil";

interface IUsers {
  id: number;
  dateAdded: string;
  userId: string;
  userName: string;
  accountSlug: string;
  userEmail: string;
  hasToken: boolean;
  planId: string | null;
  isAdmin: boolean;
  isViewOnly: boolean;
  isTrial: boolean;
  credit: number;
  usedCredits: number;
  lastExtractId: number;
  lastExtractDate: string;
}

const Inactive = () => {
  useSignal(0);

  const [days, setDays] = useState<number>(1);
  const [inactive, setInactive] = useState<IUsers[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 50 });

  const columns: GridColDef[] = [
    {
      field: "id",
      type: "number",
      headerAlign: "right",
      headerName: "#",
      width: 50,
    },
    {
      field: "dateAdded",
      type: "string",
      headerAlign: "left",
      headerName: "Date Added",
      sortable: true,
      width: 200,
    },
    {
      field: "userId",
      type: "string",
      headerAlign: "left",
      headerName: "User ID",
      sortable: true,
      width: 100,
    },
    {
      field: "accountSlug",
      type: "string",
      headerAlign: "left",
      headerName: "Slug",
      sortable: true,
      width: 150,
    },
    {
      field: "userName",
      type: "string",
      headerAlign: "left",
      headerName: "User Name",
      sortable: true,
      width: 150,
    },
    {
      field: "userEmail",
      type: "string",
      headerAlign: "left",
      headerName: "Email",
      sortable: true,
      width: 200,
    },
    {
      field: "hasToken",
      type: "boolean",
      headerAlign: "center",
      headerName: "Has Token",
      sortable: true,
      width: 100,
    },
    {
      field: "isAdmin",
      type: "boolean",
      headerAlign: "center",
      headerName: "Is Admin",
      sortable: true,
      width: 100,
    },
    {
      field: "isViewOnly",
      type: "boolean",
      headerAlign: "center",
      headerName: "Is View Only",
      sortable: true,
      width: 120,
    },
    {
      field: "isTrial",
      type: "boolean",
      headerAlign: "center",
      headerName: "Trial",
      sortable: true,
      width: 80,
    },
    {
      field: "planId",
      type: "string",
      headerAlign: "left",
      headerName: "Plan Id",
      sortable: true,
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "free" ? "success" : "warning"}
          sx={{ width: "100%", justifyContent: "center" }}
        />
      ),
      cellClassName: (params) => getPlanClass(params.value),
    },
    {
      field: "usedCredits",
      type: "number",
      headerAlign: "right",
      headerName: "Used",
      sortable: true,
      width: 80,
    },
    {
      field: "credit",
      type: "number",
      headerAlign: "right",
      headerName: "Credit",
      sortable: true,
      width: 80,
    },
    {
      field: "lastExtractId",
      type: "string",
      headerAlign: "left",
      headerName: "Extract Id",
      sortable: true,
      width: 100,
    },
    {
      field: "lastExtractDate",
      type: "string",
      headerAlign: "left",
      headerName: "Last Extract Date",
      sortable: true,
      width: 200,
    },
  ];

  const getPlanClass = (status: string) => {
    switch (status) {
      case "free":
        return "status-completed";
      case "basic":
        return "status-pending";
      case "pro":
        return "status-cancelled";
      case "ultimate":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const convertUTCToLocal = (utcDateStr: string) => {
    return moment
      .utc(utcDateStr) // Parse as UTC
      .tz(moment.tz.guess())
      .format("MMMM D, YYYY, HH:mm:ss");
  };

  const getInactiveUser = async () => {
    setLoading(true);
    setInactive([]);
    axios
      .get(`/api/v1/reports/${app.value}/inactive?inactivedays=${days}`)
      .then((response) => {
        // handle success
        console.log(response.data);
        setPaginationModel({
          page: response.data.currentPage,
          pageSize: response.data.pageSize,
        });

        let usersList: IUsers[] = response.data.data || [];
        usersList = usersList.map((list, index) => ({
          ...list,
          id: index + 1,
          planId: parsePlan(app.value, list.planId),
          dateAdded: convertUTCToLocal(list.dateAdded),
          lastExtractDate: convertUTCToLocal(list.lastExtractDate),
        }));
        setInactive(usersList);
      })
      .catch((error) => {
        // handle error
        console.log(error);
        setInactive([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useSignalEffect(() => {
    if (days) {
      getInactiveUser();
    }
  });

  return (
    <div className="row mt-2">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-lg-flex">
              <div>
                <h5 className="mb-0">Inactive</h5>
              </div>
              <div className="ms-auto my-auto mt-lg-0 mt-4">
                <div className="ms-auto my-auto">
                  <button
                    type="button"
                    className="btn btn-icon-only btn-rounded btn-secondary mb-0 ms-2 btn-sm d-flex align-items-center justify-content-center ms-auto"
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    title="Refresh"
                    onClick={() => getInactiveUser()}
                  >
                    <i className="material-symbols-rounded text-md">sync</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="row mt-0 mb-2">
              <div className="col-sm-auto p-1 align-middle">
                <label className="fw-bold text-dark m-1">
                  Select Inactive Days:
                </label>
              </div>
              <div className="col-1 p-0  m-0">
                <select
                  className="form-control text-left"
                  onChange={(e) => setDays(Number(e.currentTarget.value))}
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="30">30</option>
                </select>
              </div>
            </div>
            <Paper sx={{ height: "550px", width: "100%" }}>
              <DataGrid
                loading={loading}
                rows={inactive}
                columns={columns}
                slots={{ toolbar: GridToolbar }}
                initialState={{ pagination: { paginationModel } }}
                pageSizeOptions={[10, 20, 30, 40, 50]}
                localeText={{ noRowsLabel: "No data available to display" }}
                disableRowSelectionOnClick
                sx={{ border: 0 }}
              />
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Inactive;
